import {DateTime} from "luxon";

const isMillis = (n) => Math.abs(n) >= 1e12 || String(Math.floor(n)).length > 10;

const toUtcDateTime = (value, zone) => {
    if (value == null || value === "") return DateTime.invalid("Invalid");

    if (value instanceof Date) {
        return DateTime.fromJSDate(value, {zone: zone === "utc" ? "utc" : undefined});
    }

    const n = Number(value);
    if (!Number.isNaN(n) && Number.isFinite(n)) {
        const base = isMillis(n)
            ? DateTime.fromMillis(n, {zone: "utc"})
            : DateTime.fromSeconds(n, {zone: "utc"});
        return base.setZone(zone === "utc" ? "utc" : undefined);
    }

    return DateTime.fromISO(String(value), {zone: "utc"}).setZone(zone === "utc" ? "utc" : undefined);
};

const ordinalSuffix = (d) => {
    const v = d % 100;
    if (v >= 11 && v <= 13) return "th";
    switch (d % 10) {
        case 1:
            return "st";
        case 2:
            return "nd";
        case 3:
            return "rd";
        default:
            return "th";
    }
};

const fmtOrd = (dt, monFmt = "MMM", yrFmt = "yyyy") =>
    `${dt.toFormat(monFmt)} ${dt.day}${ordinalSuffix(dt.day)} ${dt.toFormat(yrFmt)}`;

export const parseToUtcDateTime = (value) => toUtcDateTime(value, "utc");
export const parseToLocalDateTime = (value) => parseToUtcDateTime(value).toLocal();

export function formatCompactDateUtc(value) {
    if (value == null) return "";
    const dt = value instanceof Date ? DateTime.fromJSDate(value, {zone: "utc"}) : parseToUtcDateTime(value);
    return fmtOrd(dt);
}

export function getRelativeTime(value, base = DateTime.now()) {
    if (value == null) return "";
    const dt = parseToLocalDateTime(value);
    const now = base.toLocal();
    return dt.toRelative({base: now}) ?? "";
}

export function formatDateOrToday(value, base = DateTime.now()) {
    if (value == null) return "";
    const dt = parseToLocalDateTime(value);
    const now = base.toLocal();
    return dt.hasSame(now, "day") ? "today" : dt.toFormat("MM/dd/yyyy");
}

export function formatDate(value) {
    if (value == null) return "";
    return parseToLocalDateTime(value).toFormat("MMM d, yyyy");
}

export function formatTime24h(value) {
    if (value == null) return "";
    return parseToLocalDateTime(value).toFormat("HH:mm");
}

export const toUtcStartOfDaySeconds = (value) => {
    if (!value) return 0;
    const dt = value instanceof Date ? DateTime.fromJSDate(value) : parseToLocalDateTime(value);
    return dt.startOf("day").toUTC().toSeconds();
};

export function formatWeekdayAndMonth(value) {
    if (value == null) return "";
    const dt = parseToLocalDateTime(value);
    return `${dt.toFormat("ccc LLL")} ${dt.day}${ordinalSuffix(dt.day)}`;
}

export function formatLongDate(value) {
    if (value == null) return "";
    return parseToLocalDateTime(value).toFormat("MMMM d, yyyy");
}

export function getDaysUntil(value) {
    if (value == null) return null;
    const target = parseToLocalDateTime(value);
    const now = DateTime.now().toLocal();
    return target.diff(now, "days").as("days");
}

export function formatTime12h(value) {
    if (value == null) return "";
    return parseToLocalDateTime(value).toFormat("hh:mm a");
}

export function formatTimeWithSeconds(value) {
    if (value == null) return "";
    return parseToLocalDateTime(value).toFormat("HH:mm:ss");
}

export function formatUtcDate(utcSeconds) {
    if (utcSeconds == null) return "";
    return DateTime.fromSeconds(Number(utcSeconds), {zone: "utc"}).toFormat("MMM d, yyyy");
}

export function formatDateTime12h(value) {
    if (value == null) return "";
    return parseToLocalDateTime(value).toFormat("MMM dd yyyy, hh:mm a");
}

export function formatDateWithOrdinal(value) {
    if (value == null) return "";
    return fmtOrd(parseToLocalDateTime(value));
}

export const DateUtils = {
    parse: {
        toLocalDateTime: parseToLocalDateTime,
        toUtcDateTime: parseToUtcDateTime
    },
    format: {
        date: formatDate,
        utcDate: formatUtcDate,
        dateOrToday: formatDateOrToday,
        time24h: formatTime24h,
        time12h: formatTime12h,
        timeWithSeconds: formatTimeWithSeconds,
        longDate: formatLongDate,
        weekdayAndMonth: formatWeekdayAndMonth,
        dateTime12h: formatDateTime12h,
        dateWithOrdinal: formatDateWithOrdinal,
        compactDateUtc: formatCompactDateUtc,
    },
    diff: {relativeTime: getRelativeTime, daysUntil: getDaysUntil},
    convert: {toUtcStartOfDaySeconds},
};
